import { useState } from "react";
import { ChevronDown, ChevronUp, Plus, Trash2 } from "lucide-react";
import { MENU_ITEM_IDS, MENU_PRESETS, SECTION_BY_ID, type MenuGroup } from "@clevis/shared-types";
import { Button, Card, CardTitle, Input, Select, cn } from "@clevis/shared-ui";
import { call, CallError } from "../lib/callFunction";
import { MORE_GROUP, STANDARD_GROUPS, useMenuLayout } from "../hooks/useMenuLayout";

// The owner's menu for the whole company: which groups the left menu has,
// what they're called, their order, and which pages sit in each. Everyone
// gets it; each person can still reorder inside it and pin Favorites.
// Who can see a page at all is still the access settings above.

const labelOf = (id: string) => SECTION_BY_ID.get(id)?.label ?? id;
const moved = <T,>(list: T[], from: number, to: number): T[] => {
  if (to < 0 || to >= list.length) return list;
  const next = [...list];
  const [x] = next.splice(from, 1);
  next.splice(to, 0, x!);
  return next;
};
const clone = (g: MenuGroup[]) => g.map((x) => ({ ...x, items: [...x.items] }));
const same = (a: MenuGroup[], b: MenuGroup[]) => JSON.stringify(a.map((g) => [g.label.trim(), g.items])) === JSON.stringify(b.map((g) => [g.label.trim(), g.items]));

export function CompanyMenuEditor({ tenantId, preview = false }: { tenantId: string; /** Dev gallery: render without waiting for Firestore. */ preview?: boolean }) {
  const { groups: saved, loaded } = useMenuLayout(tenantId);
  const base = saved ?? STANDARD_GROUPS;
  const [draft, setDraft] = useState<MenuGroup[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const groups = draft ?? base;
  const placed = new Set(groups.flatMap((g) => g.items));
  const unplaced = MENU_ITEM_IDS.filter((id) => !placed.has(id));
  const dirty = draft !== null && !same(draft, base);

  const edit = (fn: (g: MenuGroup[]) => MenuGroup[]) => {
    setNote("");
    setError("");
    setDraft((d) => fn(clone(d ?? base)));
  };
  const moveTo = (id: string, to: string) =>
    edit((gs) => gs.map((g) => ({ ...g, items: g.id === to ? [...g.items.filter((x) => x !== id), id] : g.items.filter((x) => x !== id) })));

  async function save(next: MenuGroup[] | null) {
    if (next && next.some((g) => !g.label.trim())) return setError("Give every group a name.");
    setBusy(true);
    setError("");
    try {
      await call("setMenuLayout", { tenantId, groups: next ? next.map((g) => ({ ...g, label: g.label.trim() })) : null });
      setDraft(null);
      setNote(next ? "Saved. Everyone's menu follows it now." : "Back to the Clevis standard menu.");
    } catch (err) {
      const e = err as CallError;
      setError(e.hint ? `${e.message} ${e.hint}` : e.message);
    } finally {
      setBusy(false);
    }
  }

  const presetActive = (k: keyof typeof MENU_PRESETS) => same(groups, MENU_PRESETS[k].groups);
  // Picking another group moves the page there (the tick marks where it is now).
  const moveOptions = () => (
    <>
      {groups.map((g, i) => (
        <option key={g.id} value={g.id}>
          {g.label.trim() || `Group ${i + 1}`}
        </option>
      ))}
      <option value={MORE_GROUP.id}>No group (under More)</option>
    </>
  );

  if (!loaded && !preview) return null;
  return (
    <Card className="space-y-3" id="company-menu">
      <div>
        <CardTitle className="mb-0">Company menu</CardTitle>
        <p className="text-xs text-steel">
          How the left menu is grouped for everyone. Rename groups, change their order and move pages between them.
          People can still reorder their own menu and pin Favorites. Who can open a page at all is still set above.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="text-steel">Start from:</span>
        {(Object.keys(MENU_PRESETS) as Array<keyof typeof MENU_PRESETS>).map((k) => (
          <button
            key={k}
            type="button"
            className={cn(
              "rounded-full border px-2.5 py-1",
              presetActive(k) ? "border-brandorange bg-brandorange/10 text-brandorange-dark" : "border-steel/30 text-ink hover:border-steel",
            )}
            onClick={() => edit(() => clone(MENU_PRESETS[k].groups))}
          >
            {MENU_PRESETS[k].label}
          </button>
        ))}
      </div>

      <div className="space-y-2">
        {groups.map((g, gi) => (
          <div key={g.id} className="rounded-lg border border-steel/20">
            <div className="flex items-center gap-1.5 bg-cream/60 px-2 py-1.5">
              <Input
                className="h-7 flex-1 py-0 text-sm font-semibold"
                value={g.label}
                maxLength={40}
                placeholder="Group name"
                aria-label="Group name"
                onChange={(e) => edit((gs) => gs.map((x) => (x.id === g.id ? { ...x, label: e.target.value } : x)))}
              />
              <span className="shrink-0 text-[11px] text-steel">{g.items.length} page{g.items.length === 1 ? "" : "s"}</span>
              <button type="button" className="p-1 text-steel hover:text-ink disabled:opacity-30" disabled={gi === 0} onClick={() => edit((gs) => moved(gs, gi, gi - 1))} aria-label={`Move ${g.label} up`}>
                <ChevronUp size={14} />
              </button>
              <button type="button" className="p-1 text-steel hover:text-ink disabled:opacity-30" disabled={gi === groups.length - 1} onClick={() => edit((gs) => moved(gs, gi, gi + 1))} aria-label={`Move ${g.label} down`}>
                <ChevronDown size={14} />
              </button>
              <button
                type="button"
                className="p-1 text-steel hover:text-danger"
                onClick={() => edit((gs) => gs.filter((x) => x.id !== g.id))}
                aria-label={`Remove ${g.label}`}
                title={g.items.length ? "Remove the group (its pages show under More until you place them)" : "Remove the group"}
              >
                <Trash2 size={13} />
              </button>
            </div>
            {g.items.length === 0 ? <p className="px-3 py-1.5 text-xs text-steel">Empty. Move pages here, or remove the group (empty groups aren't saved).</p> : null}
            <div className="divide-y divide-steel/10">
              {g.items.map((id, ii) => (
                <div key={id} className="flex h-9 items-center gap-1.5 px-3">
                  <span className="min-w-0 flex-1 truncate text-sm text-ink">{labelOf(id)}</span>
                  <Select className="w-64 py-0.5 text-xs" value={g.id} aria-label={`Group for ${labelOf(id)}`} onChange={(e) => moveTo(id, e.target.value)}>
                    {moveOptions()}
                  </Select>
                  <button type="button" className="p-1 text-steel hover:text-ink disabled:opacity-30" disabled={ii === 0} onClick={() => edit((gs) => gs.map((x) => (x.id === g.id ? { ...x, items: moved(x.items, ii, ii - 1) } : x)))} aria-label={`Move ${labelOf(id)} up`}>
                    <ChevronUp size={13} />
                  </button>
                  <button type="button" className="p-1 text-steel hover:text-ink disabled:opacity-30" disabled={ii === g.items.length - 1} onClick={() => edit((gs) => gs.map((x) => (x.id === g.id ? { ...x, items: moved(x.items, ii, ii + 1) } : x)))} aria-label={`Move ${labelOf(id)} down`}>
                    <ChevronDown size={13} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}

        {unplaced.length ? (
          <div className="rounded-lg border border-dashed border-steel/30">
            <div className="px-3 py-1.5 text-xs font-semibold text-steel">Not in a group: these show under “More” at the bottom of the menu</div>
            <div className="divide-y divide-steel/10">
              {unplaced.map((id) => (
                <div key={id} className="flex h-9 items-center gap-1.5 px-3">
                  <span className="min-w-0 flex-1 truncate text-sm text-ink">{labelOf(id)}</span>
                  <Select className="w-64 py-0.5 text-xs" value={MORE_GROUP.id} aria-label={`Group for ${labelOf(id)}`} onChange={(e) => e.target.value !== MORE_GROUP.id && moveTo(id, e.target.value)}>
                    {moveOptions()}
                  </Select>
                  <span className="w-[52px] shrink-0" />
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {groups.length < 12 ? (
          <button
            type="button"
            className="flex items-center gap-1 text-xs font-semibold text-brandorange-dark hover:underline"
            onClick={() => edit((gs) => [...gs, { id: `g${Date.now().toString(36)}`, label: "", items: [] }])}
          >
            <Plus size={13} /> Add a group
          </button>
        ) : null}
      </div>

      {error ? <p className="text-xs text-danger">{error}</p> : null}
      <div className="flex flex-wrap items-center gap-2">
        <Button busy={busy} disabled={!dirty} onClick={() => void save(groups.filter((g) => g.items.length > 0))}>
          Save company menu
        </Button>
        {dirty ? (
          <Button variant="ghost" onClick={() => setDraft(null)}>
            Undo changes
          </Button>
        ) : null}
        {saved && !dirty ? (
          <Button variant="ghost" busy={busy} onClick={() => void save(null)}>
            Back to Clevis standard
          </Button>
        ) : null}
        {note ? <span className="text-xs text-steel">{note}</span> : null}
      </div>
    </Card>
  );
}
